import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';

/**
 * Sparkle Data Interface
 */
export interface ISparkleData {
	/** values */
	values: number[];
}

/**
 * SensorData Interface
 */
export interface ISensorData {
	/** timestamp */
	timestamp: number;
	/** sampleDate */
	sampleDate: string;
	/** Sensor DataValues */
	[key: string]: number | null | string; // dynamic keys with number or null values
}

/**
 * DataPoint Interface
 */
export interface IDataPoint {
	/** DataPointID */
	DataPointID: number;
	/** publicID */
	publicID: string;
	/** SensorName */
	SensorName: string;
	/** SensorID */
	SensorID: number;
	/** PVName */
	PVName: string;
	/** PVDescription */
	PVDescription: string;
	/** Datapoint short description */
	DPTitle: string;
	/** SI */
	SI: string;
	/** SI Index */
	SIIndex: number;
	/** PV */
	PV: number;
	/** RawMin */
	RawMin: number;
	/** RawMax */
	RawMax: number;
	/** EngMin */
	EngMin: number;
	/** EngMax */
	EngMax: number;
	/** Data Last Updated */
	LastUpdated: number;
	/** Last Modified */
	Modified: number;
	/** Webhook ID */
	WebhookID: number;
	/** Datapoint Image */
	ImageURL: string;
	/** Datapoint Offline */
	Offline: boolean
	/** Primary Index */
	PrimaryIndex: boolean;
	/** Enabled */
	Enabled: boolean;
	/** LastValueReceived */
	LastValueReceived: number;
	/** Enable Offline Notifications*/
	EnableOffline: boolean;
	/** IsCumulative */
	IsCumulative: boolean;
	/** Series Colour */
	SeriesColor: string;
	/** Is a Binary Datapoint */
	IsBinary: boolean;
	/** Binary On Text Value */
	BinaryOnValue: string;
	/** Binary Off Text Value */
	BinaryOffValue: string;
	/** Binary Threshold Value */
	BinaryThresholdValue: number;
}

/**
 * DataPoint
 */
export class DataPoint implements IDataPoint {

	constructor(
		public DataPointID: number = 0,
		public publicID: string = "",
		public SensorName: string = "",
		public SensorID: number = 0,
		public PVName: string = "",
		public PVDescription: string = "",
		public DPTitle: string = "",
		public SI: string = "",
		public SIIndex: number = 0,
		public PV: number = 0,
		public RawMin: number = 0,
		public RawMax: number = 0,
		public EngMin: number = 0,
		public EngMax: number = 0,
		public LastUpdated: number = 0,
		public Modified: number = 0,
		public WebhookID: number = 0,
		public ImageURL: string = "",
		public Offline: boolean = false,
		public PrimaryIndex: boolean = false,
		public Enabled: boolean = true,
		public LastValueReceived: number = 0,
		public EnableOffline: boolean = false,
		public IsCumulative: boolean = false,
		public SeriesColor: string = "",
		public IsBinary: boolean = false,
		public BinaryOnValue: string = 'On',
		public BinaryOffValue: string = 'Off',
		public BinaryThresholdValue: number = 0.5

	) { }
}

/**
 * IAssetDataPoint Interface
 * Extends the required fields from IDatapoint
 */
export interface IAssetDataPoint extends IDataPoint {
	/** ShowOnTile */
	ShowOnTile: boolean;
	/** ShowDeltaValue */
	ShowDeltaValue: boolean
	/** Series Colour */
	SeriesColor: string
}

/**
 * DataPoint
 */
export class AssetDataPoint implements IAssetDataPoint {

	constructor(
		public DataPointID: any = 0,
		public publicID: string = "",
		public SensorName: string = "",
		public SensorID: number = 0,
		public PVName: string = "",
		public PVDescription: string = "",
		public DPTitle: string = "",
		public SI: string = "",
		public SIIndex: number = 0,
		public PV: number = 0,
		public RawMin: number = 0,
		public RawMax: number = 0,
		public EngMin: number = 0,
		public EngMax: number = 0,
		public LastUpdated: number = 0,
		public Modified: number = 0,
		public WebhookID: number = 0,
		public ImageURL: string = "",
		public Offline: boolean = false,
		public PrimaryIndex: boolean = false,
		public Enabled: boolean = true,
		public ShowOnChart: boolean = false,
		public LastValueReceived: number = 0,
		public EnableOffline: boolean = false,
		public SeriesID: number = 0,
		public ShowOnTile: boolean = false,
		public IsCumulative: boolean = false,
		public ShowDeltaValue: boolean = false,
		public SeriesColor: string = "",
		public IsBinary: boolean = false,
		public BinaryOnValue: string = "On",
		public BinaryOffValue: string = "Off",
		public BinaryThresholdValue: number = 0.5
	) { }
}

/**
 * Injectable
 */
@Injectable({
	providedIn: 'root'
})
export class DatapointsDataService {

	/** apiDataPoints */
	private apiDataPoints = "api/{0}/organisation/{1}";

	/**
	 * Default Constructor
	 * @param http Imports the 'HttpClient' Object for internet requests
	 * @param globalFunctions Global Functions
	 * @param siteSubscriptions site Subscriptions
	 */
	constructor(
		private http: HttpClient,
		public globalFunctions: GlobalFunctions,
		public siteSubscriptions: siteSubscriptions
	) { }

	/**
	* Get a list of DataPoints
	* @param AccountGUID
	* @param orgUID
	* @param dataPointID Optional Datapoint ID
	* @param queryParams Optional Query Parameters
	* @returns Observable<any>
	*/
	getDataPoints$(AccountGUID: string, orgUID: string, dataPointID: any = 0, queryParams: {} = {}): Observable<any> {

		let url = this.apiDataPoints;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/datapoints`;

		if (dataPointID != 0) {
			if (typeof dataPointID == "string") url += "/many"
			url = url + `/${dataPointID}`;
		};

		return this.globalFunctions.fetchAPI(this.http, url, "GET", queryParams);
	}

	/**
	* Get a list of offline DataPoints
	* @param AccountGUID
	* @param orgUID
	* @returns Observable<any>
	*/
	getOfflineDataPoints$(AccountGUID: string, orgUID: string): Observable<any> {
		let data = {}

		let url = this.apiDataPoints;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/DataPoints/offline`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	* Get a list of DataPoint Values
	* @param AccountGUID
	* @param orgUID
	* @param dataPointID
	* @param queryParams Optional Query Parameters
	* @returns Observable<any>
	*/
	getDataPointValues$(AccountGUID: string, orgUID: string, dataPointID: number, queryParams: {} = {}): Observable<any> {

		let url = this.apiDataPoints;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${dataPointID}/${url}/datapointValues`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", queryParams);
	}

	/**
	 * Patching a datapoint
	 * @param AccountGUID
	 * @param orgUID
	 * @param datapoint
	 * @returns
	 */
	patchDataPoint$(AccountGUID: string, orgUID: string, datapoint: DataPoint): Observable<any> {
		let data = JSON.stringify(datapoint);

		let url = this.apiDataPoints;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/DataPoints/${datapoint.DataPointID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

}
